import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import MainURL from '../../MainURL';

const CATEGORIES = ['정기심방', '신규심방', '경조사심방', '요청심방', '병원심방', '기타'];
const STATUS_OPTIONS = ['완료', '예정'];

type VisitationRow = {
  id: number;
  memberId: number;
  memberName: string;
  visitDate: string;
  category: string;
  visitorName: string;
  content: string;
  status: string;
};

type VisitationForm = {
  memberId: number;
  memberName: string;
  visitDate: string;
  category: string;
  visitorName: string;
  content: string;
  status: string;
};

type MemberOption = { id: number; name: string; position: string; department: string };

function emptyForm(): VisitationForm {
  return {
    memberId: 0,
    memberName: '',
    visitDate: '',
    category: CATEGORIES[0],
    visitorName: '',
    content: '',
    status: STATUS_OPTIONS[0],
  };
}

function currentMonthValue() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

const STATUS_BADGE: Record<string, string> = {
  완료: 'manage-badge--green',
  예정: 'manage-badge--amber',
};

export default function ManageVisitation({ authChurch }: { authChurch: string }) {
  const [month, setMonth] = useState(currentMonthValue());
  const [rows, setRows] = useState<VisitationRow[]>([]);
  const [members, setMembers] = useState<MemberOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<VisitationForm>(emptyForm());
  const [saving, setSaving] = useState(false);

  const fetchRecords = () => {
    setLoading(true);
    setError(null);
    axios
      .get(`${MainURL}/visitation`, { params: { authChurch, month } })
      .then((res) => setRows(res.data?.data ?? []))
      .catch((err) => {
        console.error('GET /visitation', err);
        setError('심방 내역을 불러오지 못했습니다.');
      })
      .finally(() => setLoading(false));
  };

  const fetchMembers = () => {
    axios
      .get(`${MainURL}/church-member`, { params: { authChurch } })
      .then((res) => setMembers(res.data?.data ?? []))
      .catch((err) => console.error('GET /church-member', err));
  };

  useEffect(() => {
    fetchRecords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authChurch, month]);

  useEffect(() => {
    fetchMembers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authChurch]);

  const openCreateForm = () => {
    setEditingId(null);
    setForm({ ...emptyForm(), visitDate: `${month}-01` });
    setFormOpen(true);
  };

  const openEditForm = (row: VisitationRow) => {
    setEditingId(row.id);
    setForm({
      memberId: row.memberId,
      memberName: row.memberName,
      visitDate: row.visitDate.slice(0, 10),
      category: row.category,
      visitorName: row.visitorName,
      content: row.content,
      status: row.status,
    });
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditingId(null);
    setForm(emptyForm());
  };

  const onMemberChange = (memberId: number) => {
    const member = members.find((m) => m.id === memberId);
    setForm({ ...form, memberId, memberName: member?.name ?? '' });
  };

  const submitForm = () => {
    if (!form.memberId || !form.visitDate || !form.category) return;
    setSaving(true);
    const payload = { authChurch, ...form };
    const request = editingId
      ? axios.put(`${MainURL}/visitation/${editingId}`, payload)
      : axios.post(`${MainURL}/visitation`, payload);

    request
      .then(() => {
        closeForm();
        fetchRecords();
      })
      .catch((err) => console.error(editingId ? 'PUT /visitation/:id' : 'POST /visitation', err))
      .finally(() => setSaving(false));
  };

  const removeRecord = (id: number) => {
    if (!window.confirm('이 심방 내역을 삭제할까요?')) return;
    axios
      .delete(`${MainURL}/visitation/${id}`, { params: { authChurch } })
      .then(() => fetchRecords())
      .catch((err) => console.error('DELETE /visitation/:id', err));
  };

  const summary = useMemo(() => {
    const done = rows.filter((r) => r.status === '완료').length;
    const planned = rows.filter((r) => r.status === '예정').length;
    return { total: rows.length, done, planned };
  }, [rows]);

  return (
    <div className="manage-panel">
      <div className="manage-panel-header">
        <div>
          <h2 className="manage-panel-title">심방관리</h2>
          <p className="manage-panel-desc">성도별 심방 일정과 기록을 관리합니다.</p>
        </div>
        <button type="button" className="manage-btn manage-btn--primary" onClick={openCreateForm}>
          + 심방 등록
        </button>
      </div>

      <div className="manage-filterbar manage-attendance-toolbar">
        <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} />
      </div>

      {loading && <div className="manage-state">불러오는 중입니다...</div>}
      {!loading && error && <div className="manage-state manage-state--error">{error}</div>}

      {!loading && !error && (
        <>
          <div className="manage-finance-summary">
            <div className="manage-finance-summary-card manage-finance-highlight-card">
              <span>{month} 전체 심방</span>
              <strong>{summary.total}건</strong>
            </div>
            <div className="manage-finance-summary-card manage-stat-card--income">
              <span>완료</span>
              <strong>{summary.done}건</strong>
            </div>
            <div className="manage-finance-summary-card manage-stat-card--expense">
              <span>예정</span>
              <strong>{summary.planned}건</strong>
            </div>
          </div>

          <section className="manage-card-block">
            <div className="manage-block-head">
              <h3 className="manage-block-title">심방 내역</h3>
              <span className="manage-block-count">{rows.length}</span>
            </div>
            <div className="manage-table-wrap">
              <table className="manage-table">
                <thead>
                  <tr>
                    <th style={{ width: 90 }}>날짜</th>
                    <th style={{ width: 100 }}>성도</th>
                    <th style={{ width: 100 }}>구분</th>
                    <th>내용</th>
                    <th style={{ width: 90 }}>심방자</th>
                    <th style={{ width: 90 }}>상태</th>
                    <th style={{ width: 140 }}>관리</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 && (
                    <tr>
                      <td colSpan={7} className="manage-table-empty">등록된 심방 내역이 없습니다.</td>
                    </tr>
                  )}
                  {rows.map((row) => (
                    <tr key={row.id}>
                      <td className="manage-table-meta">{row.visitDate.slice(0, 10)}</td>
                      <td>{row.memberName}</td>
                      <td><span className="manage-badge manage-badge--gray">{row.category}</span></td>
                      <td>{row.content}</td>
                      <td>{row.visitorName}</td>
                      <td><span className={`manage-badge ${STATUS_BADGE[row.status] ?? 'manage-badge--gray'}`}>{row.status}</span></td>
                      <td>
                        <div className="manage-row-actions">
                          <button type="button" className="manage-btn manage-btn--sm" onClick={() => openEditForm(row)}>수정</button>
                          <button type="button" className="manage-btn manage-btn--sm manage-btn--danger" onClick={() => removeRecord(row.id)}>삭제</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}

      {formOpen && (
        <div className="manage-modal-overlay" onClick={closeForm}>
          <div className="manage-modal" onClick={(e) => e.stopPropagation()}>
            <div className="manage-modal-header">
              <h3>{editingId ? '심방 내역 수정' : '심방 내역 등록'}</h3>
              <button type="button" className="manage-modal-close" onClick={closeForm}>×</button>
            </div>
            <div className="manage-modal-body">
              <div className="manage-form manage-form--grid">
                <label className="manage-field">
                  <span>성도</span>
                  <select value={form.memberId} onChange={(e) => onMemberChange(Number(e.target.value))}>
                    <option value={0}>선택</option>
                    {members.map((m) => (
                      <option key={m.id} value={m.id}>{m.name} ({[m.position, m.department].filter(Boolean).join(' · ')})</option>
                    ))}
                  </select>
                </label>
                <label className="manage-field">
                  <span>날짜</span>
                  <input type="date" value={form.visitDate} onChange={(e) => setForm({ ...form, visitDate: e.target.value })} />
                </label>
                <label className="manage-field">
                  <span>구분</span>
                  <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                    {CATEGORIES.map((category) => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </label>
                <label className="manage-field">
                  <span>심방자</span>
                  <input value={form.visitorName} onChange={(e) => setForm({ ...form, visitorName: e.target.value })} />
                </label>
                <label className="manage-field">
                  <span>상태</span>
                  <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                    {STATUS_OPTIONS.map((status) => (
                      <option key={status} value={status}>{status}</option>
                    ))}
                  </select>
                </label>
                <label className="manage-field manage-field--full">
                  <span>내용</span>
                  <input value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} />
                </label>
              </div>
            </div>
            <div className="manage-modal-footer">
              <button type="button" className="manage-btn" onClick={closeForm}>취소</button>
              <button
                type="button"
                className="manage-btn manage-btn--primary"
                onClick={submitForm}
                disabled={saving || !form.memberId || !form.visitDate}
              >
                {saving ? '저장 중...' : '저장'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
